import { useCallback, useRef } from "react";
import { useSWRConfig } from "swr";
import { fetcher } from "@/lib/fetcher";
import { summarySystemActions } from "../summary-spec-service";
import type { ApiResponse } from "../model";
import type { SystemRecord } from "../schemas/system-schemas";
import {
  getSystemDetailUrl,
  matchesSystemsCacheKey,
  SYSTEMS_LIST_URL,
} from "../system-cache";

const MAX_RETRIES = 3;
const BACKOFF_DELAYS = [2000, 4000, 8000];

// A system whose summary is too large to generate inside one request is accepted
// and generated in the background (AIS-2403), so the page waits for the stored
// summary instead of holding the request open — the gateway gives the route 10
// seconds — and instead of firing the same generation again and again.
const SUMMARY_POLL_INTERVAL_MS = 3000;
const SUMMARY_POLL_TIMEOUT_MS = 5 * 60 * 1000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Waits for a background generation to persist its summary on the system. */
async function waitForStoredSummary(systemId: string): Promise<string | null> {
  const deadline = Date.now() + SUMMARY_POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    await delay(SUMMARY_POLL_INTERVAL_MS);

    try {
      const response = await fetcher<ApiResponse<SystemRecord[]>>(
        getSystemDetailUrl(systemId),
      );
      const summary = response.data?.find(
        (system) => system.system_id === systemId,
      )?.ai_summary;
      if (summary) return summary;
    } catch (error) {
      // A failed poll is not fatal: the generation may still be running.
      console.error("Failed to poll the generated system summary:", error);
    }
  }

  return null;
}

async function callSummaryWithRetry(systemId: string): Promise<string | null> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const result = await summarySystemActions(systemId);
      if (result.content_summary) return result.content_summary;
      // Accepted for background generation: wait for the stored summary.
      return waitForStoredSummary(systemId);
    } catch (error) {
      lastError = error;
      if (attempt < MAX_RETRIES) {
        await delay(BACKOFF_DELAYS[attempt]);
      }
    }
  }

  throw lastError;
}

/**
 * Hook to generate AI summary for a system.
 * Starts the generation, waits until the summary is stored (a large system is
 * generated in the background), and patches the SWR cache so the UI updates.
 */
export function useGenerateSystemSummary() {
  const { mutate } = useSWRConfig();
  const abortRef = useRef(0);

  const generateSummary = useCallback(
    async (
      systemId: string,
    ): Promise<string | null> => {
      const callId = ++abortRef.current;

      try {
        const aiSummary = await callSummaryWithRetry(systemId);

        // Abort if a newer call has been made
        if (callId !== abortRef.current) return null;

        // The generation did not finish inside the wait window; leave the honest
        // empty state instead of caching a missing summary.
        if (!aiSummary) return null;

        // Patch SWR cache so UI updates immediately
        const patchSummary = (
          current: ApiResponse<SystemRecord[]> | undefined,
        ) => {
          if (!current?.data?.length) return current;
          const data = current.data.map((system) =>
            system.system_id === systemId
              ? { ...system, ai_summary: aiSummary }
              : system,
          );
          return { ...current, data };
        };

        mutate<ApiResponse<SystemRecord[]>>(
          (key) => matchesSystemsCacheKey(key, SYSTEMS_LIST_URL),
          patchSummary,
          { revalidate: false },
        );

        mutate<ApiResponse<SystemRecord[]>>(
          (key) =>
            matchesSystemsCacheKey(key, getSystemDetailUrl(systemId)),
          patchSummary,
          { revalidate: false },
        );

        return aiSummary;
      } catch (error) {
        console.error("Failed to generate system summary:", error);
        return null;
      }
    },
    [mutate],
  );

  return generateSummary;
}
